/**
 * ChartTooltip — карточка значений под курсором.
 *
 * Обычный HTML, а не SVG: тултипу нужны шрифты, отступы и переносы системы, а
 * не своя вёрстка внутри канвы. Побочная выгода — он тестируется как любой
 * компонент, без монтирования графика.
 *
 * Показывает ВСЕ серии точки, а не только ту, над которой курсор: читатель
 * подводит мышь, чтобы сравнить, и вторая половина сравнения не должна
 * требовать второго движения.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusM} from 'ui/tokens/ts/radii';

import {seriesVar, colorVar} from './tokens';
import type {ChartSeries} from './types';

const Card = styled(Box)(({theme}) => ({
  background: surfaceVar('surface1'),
  border: `1px solid ${surfaceVar('surface2')}`,
  borderRadius: RadiusM,
  padding: theme.spacing(3),
  minWidth: 160,
  pointerEvents: 'none',
}));

const Caption = styled(Box)({
  ...text('label.s'),
  color: surfaceVar('onSurfaceLight'),
});

const Label = styled(Box)({
  ...text('body.s'),
  color: surfaceVar('onSurfaceLight'),
  whiteSpace: 'nowrap',
});

const Value = styled(Box)({
  ...text('label.m'),
  color: surfaceVar('onSurface'),
  fontVariantNumeric: 'tabular-nums lining-nums',
  fontFeatureSettings: '"lnum" 1, "tnum" 1',
  whiteSpace: 'nowrap',
});

const Marker = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$color: string}>(({$color}) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: $color,
  flexShrink: 0,
}));

/**
 * Пустое место на ширину маркера — чтобы подписи всех строк стояли в одной
 * колонке, даже если у величины нет своего цвета.
 */
const MarkerSpacer = styled(Box)({
  width: 8,
  flexShrink: 0,
});

/** Что Recharts кладёт в `payload` элемента тултипа. */
interface TooltipEntry {
  dataKey?: string | number;
  value?: number | string | (number | string)[];
  payload?: unknown;
}

export interface ChartTooltipProps {
  /** Проставляется Recharts. */
  active?: boolean;
  /** Проставляется Recharts. */
  payload?: TooltipEntry[];
  /** Проставляется Recharts. */
  label?: string | number;
  series: ChartSeries[];
  xFormat?: (value: string | number) => string;
}

interface TooltipRow {
  key: string;
  label: string;
  /** `null` у величины, которой на канве нет — маркеру нечего называть. */
  color: string | null;
  value: string;
}

const defaultFormat = (value: number) => value.toLocaleString('ru-RU');

export function ChartTooltip({active, payload, label, series, xFormat}: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  // Идём по series, а не по payload: порядок в тултипе обязан совпадать с
  // порядком в легенде, иначе взгляд каждый раз ищет строку заново.
  // Строка данных под курсором — из неё берутся величины, которые не рисуются
  // (суммы стека): в `payload` попадают только отрисованные серии.
  const row = payload[0]?.payload as Record<string, unknown> | undefined;

  const rows: TooltipRow[] = series
    .map((s, index): TooltipRow | null => {
      const entry = payload.find(p => p.dataKey === s.id);
      const raw = s.tooltipOnly ? (row?.[s.id] as number | string | undefined) : entry?.value;
      if (raw == null || Array.isArray(raw)) return null;
      const numeric = typeof raw === 'number' ? raw : Number(raw);
      const formatted = Number.isFinite(numeric)
        ? (s.format ?? defaultFormat)(numeric)
        : String(raw);
      return {
        key: s.id,
        label: s.label,
        // Цвет серии уважается и у величин, которые сами по себе не рисуются:
        // у них на канве может быть НЕ линия, а подложка — например, заливка
        // выходного дня. Маркеру тогда есть что называть, и он связывает
        // строку с отметкой. Слот палитры по номеру такой строке не даётся:
        // без явного цвета ей на канве действительно не соответствует ничего.
        color: s.color ? colorVar(s.color) : s.tooltipOnly ? null : seriesVar(index),
        value: s.unit ? `${formatted} ${s.unit}` : formatted,
      };
    })
    .filter((row): row is TooltipRow => row !== null);

  if (!rows.length) return null;

  return (
    <Card>
      <Stack gap='xs'>
        {label != null && <Caption>{xFormat ? xFormat(label) : label}</Caption>}
        {rows.map(row => (
          <Row key={row.key} gap='s' justify='between' alignY='center'>
            <Row gap='xs' alignY='center' width='hug'>
              {row.color ? <Marker $color={row.color} /> : <MarkerSpacer />}
              <Label>{row.label}</Label>
            </Row>
            <Value>{row.value}</Value>
          </Row>
        ))}
      </Stack>
    </Card>
  );
}
